import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, MapPin, Linkedin, Instagram, Facebook } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SchemaOrg, buildOrganizationSchema, buildWebsiteSchema } from './SchemaOrg';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_777e38d8-0271-4c91-9972-204898c3a91a/artifacts/4skld8z5_Screenshot_2026-03-24_000951-removebg-preview.png";

const servicesMenu = [
  {
    title: "Core Services",
    items: [
      { name: "AI Solutions", href: "/ai-solutions", desc: "Enterprise AI at SMB-friendly prices" },
      { name: "Product Engineering", href: "/services/product-engineering", desc: "Web, mobile & SaaS development" },
      { name: "Business Automation", href: "/services/business-automation", desc: "Workflow & process automation" },
      { name: "ERP & Ecommerce", href: "/services/erp-ecommerce", desc: "Unified commerce systems" },
      { name: "Consulting & Advisory", href: "/services/consulting", desc: "Strategic guidance & roadmaps" },
    ]
  },
  {
    title: "Digital Marketing",
    items: [
      { name: "Marketing Overview", href: "/services/digital-marketing", desc: "Full-service marketing engine" },
      { name: "SEO & Content", href: "/services/digital-marketing/seo", desc: "AI-powered, GEO & LLM ready" },
      { name: "Social Media", href: "/services/digital-marketing/social-media", desc: "Done-for-you content & community" },
      { name: "PPC & Paid Ads", href: "/services/digital-marketing/ppc", desc: "Google, Meta, LinkedIn campaigns" },
      { name: "Brand & Analytics", href: "/services/digital-marketing/brand-strategy", desc: "Positioning + unified dashboards" },
    ]
  },
  {
    title: "SEO Specialties",
    items: [
      { name: "AI SEO / GEO", href: "/services/digital-marketing/seo/ai-seo", desc: "Google AI Overviews & GEO" },
      { name: "LLM Optimization", href: "/services/digital-marketing/seo/llm-optimization", desc: "ChatGPT, Claude, Gemini citations" },
      { name: "Ecommerce SEO", href: "/services/digital-marketing/seo/ecommerce", desc: "Product & marketplace ranking" },
      { name: "Technical SEO", href: "/services/digital-marketing/seo/technical", desc: "Core Web Vitals & schema" },
      { name: "SEO Copywriting", href: "/services/digital-marketing/seo/copywriting", desc: "Bilingual, brand-matched content" },
    ]
  }
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "AI Solutions", href: "/ai-solutions" },
    { name: "Services", href: "#", hasDropdown: true },
    { name: "Our Approach", href: "/approach" },
    { name: "About Us", href: "/about" },
    { name: "Case Studies", href: "/case-studies" },
    { name: "Resources", href: "/resources" },
    { name: "Contact", href: "/contact" },
  ];

  return (
    <header 
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100' 
          : 'bg-white'
      }`}
      data-testid="main-header"
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Blue Ribbon Technologies" 
              className="h-20 md:h-24 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-0.5" data-testid="desktop-nav">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div 
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => setIsServicesOpen(true)}
                  onMouseLeave={() => setIsServicesOpen(false)}
                >
                  <button 
                    className="flex items-center gap-1 px-3 py-2 font-outfit text-[15px] font-semibold tracking-tight whitespace-nowrap text-slate-700 hover:text-[#0A2540] transition-colors"
                    data-testid="services-dropdown-trigger"
                  >
                    {link.name}
                    <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Mega Menu */}
                  {isServicesOpen && (
                    <div 
                      className="absolute top-full left-1/2 -translate-x-1/2 w-[800px] bg-white border border-slate-100 rounded-xl shadow-xl p-6 mt-2 animate-fadeIn"
                      data-testid="services-mega-menu"
                    >
                      <div className="grid grid-cols-3 gap-8">
                        {servicesMenu.map((category) => (
                          <div key={category.title}>
                            <h3 className="font-outfit font-semibold text-[#0A2540] mb-4 text-sm uppercase tracking-wider">
                              {category.title}
                            </h3>
                            <ul className="space-y-3">
                              {category.items.map((item) => (
                                <li key={item.name}>
                                  <Link 
                                    to={item.href}
                                    className="group block"
                                    data-testid={`service-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                                  >
                                    <span className="font-medium text-slate-800 group-hover:text-[#2563EB] transition-colors">
                                      {item.name}
                                    </span>
                                    <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      <div className="mt-6 pt-6 border-t border-slate-100">
                        <Link 
                          to="/services" 
                          className="text-sm font-medium text-[#2563EB] hover:underline"
                          data-testid="view-all-services-link"
                        >
                          View All Services →
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`px-3 py-2 font-outfit text-[15px] font-semibold tracking-tight whitespace-nowrap transition-colors ${
                    location.pathname === link.href 
                      ? 'text-[#0A2540]' 
                      : 'text-slate-600 hover:text-[#0A2540]'
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/contact">
              <Button 
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-6 py-2.5 font-medium"
                data-testid="header-cta-button"
              >
                Book a Discovery Call
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            className="lg:hidden bg-white border-t border-slate-100 py-4 animate-fadeIn"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                link.hasDropdown ? (
                  <div key={link.name}>
                    <button
                      className="flex items-center justify-between w-full px-4 py-3 font-outfit text-base font-semibold tracking-tight text-slate-700"
                      onClick={() => setIsServicesOpen(!isServicesOpen)}
                    >
                      {link.name}
                      <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isServicesOpen && (
                      <div className="bg-slate-50 py-2 px-4">
                        {servicesMenu.map((category) => (
                          <div key={category.title} className="mb-4">
                            <h4 className="font-semibold text-sm text-[#0A2540] mb-2">{category.title}</h4>
                            {category.items.map((item) => (
                              <Link
                                key={item.name}
                                to={item.href}
                                className="block py-2 text-sm text-slate-600 hover:text-[#2563EB]"
                              >
                                {item.name}
                              </Link>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="px-4 py-3 font-outfit text-base font-semibold tracking-tight text-slate-700 hover:bg-slate-50"
                  >
                    {link.name}
                  </Link>
                )
              ))}
              <div className="px-4 pt-4 border-t border-slate-100 mt-2">
                <Link to="/contact">
                  <Button className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full py-3">
                    Book a Discovery Call
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#0A2540] text-white" data-testid="main-footer">
      <div className="container-custom py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <img 
              src={LOGO_URL} 
              alt="Blue Ribbon Technologies" 
              className="h-12 w-auto mb-6 brightness-0 invert"
            />
            <p className="text-slate-300 text-sm leading-relaxed mb-6">
              Empowering Canadian SMBs with affordable AI solutions and digital transformation services.
            </p>
            <div className="space-y-3">
              <a href="tel:604-267-3037" className="flex items-center gap-3 text-slate-300 hover:text-white text-sm">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span>604-267-3037</span>
              </a>
              <a href="mailto:connect@blueribbontechnology.com" className="flex items-center gap-3 text-slate-300 hover:text-white text-sm min-w-0">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span className="whitespace-nowrap text-[13px] tracking-tight">connect@blueribbontechnology.com</span>
              </a>
              <div className="flex items-start gap-3 text-slate-300 text-sm">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>1100-1200 West 73rd Avenue,<br />Vancouver, BC, V6P 6G5, Canada</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-outfit font-semibold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: "Home", href: "/" },
                { name: "AI Solutions", href: "/ai-solutions" },
                { name: "About Us", href: "/about" },
                { name: "Case Studies", href: "/case-studies" },
                { name: "Resources", href: "/resources" },
                { name: "Contact", href: "/contact" },
                { name: "Privacy Policy", href: "/privacy" },
              ].map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-slate-300 hover:text-white text-sm transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-outfit font-semibold text-lg mb-6">Services</h4>
            <ul className="space-y-3">
              {[
                { name: "AI Solutions", href: "/ai-solutions" },
                { name: "Product Engineering", href: "/services/product-engineering" },
                { name: "Business Automation", href: "/services/business-automation" },
                { name: "Digital Marketing", href: "/services/digital-marketing" },
                { name: "AI SEO / GEO", href: "/services/digital-marketing/seo/ai-seo" },
                { name: "Consulting", href: "/services/consulting" },
              ].map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-slate-300 hover:text-white text-sm transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div>
            <h4 className="font-outfit font-semibold text-lg mb-6">Ready to Transform?</h4>
            <p className="text-slate-300 text-sm mb-6">
              Book a discovery call and let's find your highest-value automation.
            </p>
            <Link to="/contact">
              <Button 
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-6 py-3 w-full"
                data-testid="footer-cta-button"
              >
                Discovery Call
              </Button>
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-700 mt-16 pt-10 pb-4 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Blue Ribbon Technologies Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-slate-400 hover:text-white text-sm">Privacy Policy</Link>
            <Link to="/terms" className="text-slate-400 hover:text-white text-sm">Terms of Service</Link>
            <div className="flex items-center gap-3 pl-6 border-l border-slate-700">
              <a
                href="https://www.linkedin.com/company/blueribbon-technologies"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#2563EB] flex items-center justify-center text-slate-300 hover:text-white transition-colors"
                data-testid="footer-linkedin-icon"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/blueribbontechnology"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#E1306C] flex items-center justify-center text-slate-300 hover:text-white transition-colors"
                data-testid="footer-instagram-icon"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.facebook.com/Blueribbontechnologies"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#1877F2] flex items-center justify-center text-slate-300 hover:text-white transition-colors"
                data-testid="footer-facebook-icon"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="mailto:connect@blueribbontechnology.com"
                aria-label="Email"
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#2563EB] flex items-center justify-center text-slate-300 hover:text-white transition-colors"
                data-testid="footer-email-icon"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Global JSON-LD schema for AI Search Optimization (AEO/GEO) */}
      <SchemaOrg data={buildOrganizationSchema()} id="schema-organization" />
      <SchemaOrg data={buildWebsiteSchema()} id="schema-website" />
      <Header />
      <main className="flex-grow">{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;
